import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Icon,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Stack,
    Switch,
    Text,
    Wrap,
    WrapItem,
    useToast,
} from "@chakra-ui/react";
import {MdExpandMore, MdSchedule} from "react-icons/md";
import {DEFAULT_AUTOPLAY, readAutoPlaySettings, writeAutoPlaySettings} from "../../../utils/MusicScheduleUtils";

const WEEKDAYS = [
    {value: 0, label: "Sun"},
    {value: 1, label: "Mon"},
    {value: 2, label: "Tue"},
    {value: 3, label: "Wed"},
    {value: 4, label: "Thu"},
    {value: 5, label: "Fri"},
    {value: 6, label: "Sat"},
];

// trackIds are stored as strings so they compare cleanly against String(id)
// regardless of whether the id is a number (API) or filename (fallback).
function AutoPlaySettingsModal({isOpen, onClose, tracks = [], onSaved}) {
    const toast = useToast();
    const [form, setForm] = useState(DEFAULT_AUTOPLAY);

    useEffect(() => {
        if (isOpen) {
            const saved = readAutoPlaySettings();
            setForm({...saved, trackIds: saved.trackIds.map(String)});
        }
    }, [isOpen]);

    const update = (patch) => setForm((prev) => ({...prev, ...patch}));

    const toggleDay = (value) =>
        update({
            days: form.days.includes(value)
                ? form.days.filter((d) => d !== value)
                : [...form.days, value].sort((a, b) => a - b),
        });

    const selectedTitles = tracks
        .filter((t) => form.trackIds.includes(String(t.id)))
        .map((t) => t.title);

    const handleSave = () => {
        if (form.enabled) {
            if (!form.fromTime || !form.toTime) {
                toast({title: "Set a start and end time", status: "warning", duration: 2500, isClosable: true});
                return;
            }
            if (form.trackIds.length === 0) {
                toast({title: "Select at least one track", status: "warning", duration: 2500, isClosable: true});
                return;
            }
        }
        writeAutoPlaySettings(form);
        toast({title: "Auto-play schedule saved", status: "success", duration: 2500, isClosable: true});
        onSaved?.(form);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>
                    <HStack spacing={2}>
                        <Icon as={MdSchedule} color="purple.500"/>
                        <Text>Auto-Play Schedule</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={5}>
                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <FormLabel mb={0}>Enable auto-play</FormLabel>
                                <Text fontSize="xs" color="gray.500">
                                    Play the selected music automatically during the time window below.
                                </Text>
                            </Box>
                            <Switch
                                colorScheme="purple"
                                isChecked={form.enabled}
                                onChange={(e) => update({enabled: e.target.checked})}
                            />
                        </FormControl>

                        <SimpleGrid columns={2} spacing={4}>
                            <FormControl isRequired={form.enabled}>
                                <FormLabel>From</FormLabel>
                                <input
                                    type="time"
                                    value={form.fromTime}
                                    onChange={(e) => update({fromTime: e.target.value})}
                                    style={{width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #E2E8F0"}}
                                />
                            </FormControl>
                            <FormControl isRequired={form.enabled}>
                                <FormLabel>To</FormLabel>
                                <input
                                    type="time"
                                    value={form.toTime}
                                    onChange={(e) => update({toTime: e.target.value})}
                                    style={{width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #E2E8F0"}}
                                />
                            </FormControl>
                        </SimpleGrid>

                        <FormControl>
                            <FormLabel>On days</FormLabel>
                            <Wrap spacing={2}>
                                {WEEKDAYS.map((day) => {
                                    const active = form.days.includes(day.value);
                                    return (
                                        <WrapItem key={day.value}>
                                            <Button
                                                size="sm"
                                                borderRadius="full"
                                                variant={active ? "solid" : "outline"}
                                                colorScheme="purple"
                                                onClick={() => toggleDay(day.value)}
                                            >
                                                {day.label}
                                            </Button>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Leave all unselected to schedule every day.
                            </Text>
                        </FormControl>

                        <FormControl isRequired={form.enabled}>
                            <FormLabel>Music</FormLabel>
                            <Menu closeOnSelect={false} matchWidth>
                                <MenuButton
                                    as={Button}
                                    w="full"
                                    textAlign="left"
                                    variant="outline"
                                    fontWeight="normal"
                                    rightIcon={<MdExpandMore/>}
                                >
                                    <Text noOfLines={1} color={form.trackIds.length ? undefined : "gray.400"}>
                                        {form.trackIds.length
                                            ? `${form.trackIds.length} selected — ${selectedTitles.join(", ")}`
                                            : "Select tracks…"}
                                    </Text>
                                </MenuButton>
                                <MenuList maxH="260px" overflowY="auto">
                                    {tracks.length === 0 ? (
                                        <Box px={3} py={2} color="gray.400" fontSize="sm">No tracks in the library yet.</Box>
                                    ) : (
                                        <MenuOptionGroup
                                            type="checkbox"
                                            value={form.trackIds}
                                            onChange={(value) => update({trackIds: Array.isArray(value) ? value : [value]})}
                                        >
                                            {tracks.map((track) => (
                                                <MenuItemOption key={track.id} value={String(track.id)}>
                                                    <Flex direction="column">
                                                        <Text fontSize="sm" noOfLines={1}>{track.title}</Text>
                                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{track.artist}</Text>
                                                    </Flex>
                                                </MenuItemOption>
                                            ))}
                                        </MenuOptionGroup>
                                    )}
                                </MenuList>
                            </Menu>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Selected tracks play in order and loop until the window ends.
                            </Text>
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="purple" onClick={handleSave}>Save schedule</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AutoPlaySettingsModal;
